import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  CalendarClock,
  Plus,
  RefreshCw,
  Calendar,
  User,
  Users,
  Clock,
  CheckCircle,
  Bell,
  FileText,
  BarChart3,
  Loader2,
  ChevronRight,
  Trash2,
  Edit,
  Play,
  Pause,
  Check,
  X,
  Upload,
  MessageSquare,
  History
} from 'lucide-react';

const TramitesRecurrentes = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tramites, setTramites] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [config, setConfig] = useState(null);
  
  // Vista
  const [activeTab, setActiveTab] = useState('activos');
  
  // Modal nuevo
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newTramite, setNewTramite] = useState({
    titulo: '',
    descripcion: '',
    tipo_tramite: 'NOTIFICACION',
    prioridad: 'MEDIA',
    recurrencia: {
      tipo: 'MENSUAL',
      dia_semana: null,
      dia_mes: 1,
      intervalo_dias: null,
      fecha_inicio: new Date().toISOString().split('T')[0],
      fecha_fin: null,
      hora_notificacion: '09:00'
    },
    responsable_id: '',
    asignados_ids: [],
    grupo_id: '',
    supervisor_id: '',
    dias_anticipacion_notificar: 3,
    notificar_si_no_completado: true,
    tags: [],
    empresa_id: ''
  });
  const [creating, setCreating] = useState(false);
  
  // Modal completar
  const [showCompletarDialog, setShowCompletarDialog] = useState(false);
  const [completarData, setCompletarData] = useState({
    tramite: null,
    fecha_ocurrencia: '',
    comentario: '',
    archivo: null
  });
  const [completing, setCompleting] = useState(false);
  
  // Modal detalle
  const [showDetalleDialog, setShowDetalleDialog] = useState(false);
  const [tramiteDetalle, setTramiteDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  
  // Modal eliminar
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadTramites();
  }, [activeTab]);

  const loadData = async () => {
    try {
      const [usersRes, gruposRes, configRes, dashboardRes] = await Promise.all([
        api.get('/users/asignables').catch(() => ({ data: [] })),
        api.get('/groups').catch(() => ({ data: [] })),
        api.get('/tramites/recurrentes/config/tipos').catch(e => { console.error('Error config recurrentes:', e); return { data: null }; }),
        api.get('/tramites/recurrentes/dashboard').catch(e => { console.error('Error dashboard recurrentes:', e); return { data: null }; })
      ]);
      setUsers(usersRes.data || []);
      setGrupos(gruposRes.data || []);
      setConfig(configRes.data);
      setDashboard(dashboardRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const loadTramites = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeTab === 'activos') {
        params.append('activo', 'true');
      } else if (activeTab === 'inactivos') {
        params.append('activo', 'false');
      }
      
      const res = await api.get(`/tramites/recurrentes?${params.toString()}`);
      setTramites(res.data);
    } catch (error) {
      console.error('Error loading tramites:', error);
      toast.error('Error al cargar trámites recurrentes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTramite = async () => {
    if (!newTramite.titulo || !newTramite.responsable_id) {
      toast.error('Complete los campos obligatorios');
      return;
    }
    
    setCreating(true);
    try {
      const payload = {
        ...newTramite,
        recurrencia: {
          ...newTramite.recurrencia,
          dia_semana: newTramite.recurrencia.tipo === 'SEMANAL' ? newTramite.recurrencia.dia_semana : null,
          dia_mes: ['MENSUAL', 'TRIMESTRAL', 'ANUAL'].includes(newTramite.recurrencia.tipo) ? newTramite.recurrencia.dia_mes : null,
          intervalo_dias: newTramite.recurrencia.tipo === 'PERSONALIZADO' ? newTramite.recurrencia.intervalo_dias : null
        }
      };
      
      const res = await api.post('/tramites/recurrentes', payload);
      toast.success(`Trámite recurrente creado. Próxima ocurrencia: ${new Date(res.data.proxima_ocurrencia).toLocaleDateString('es-ES')}`);
      setShowNewDialog(false);
      resetNewTramite();
      loadTramites();
      loadData();
    } catch (error) {
      toast.error('Error al crear trámite recurrente');
    } finally {
      setCreating(false);
    }
  };

  const resetNewTramite = () => {
    setNewTramite({
      titulo: '',
      descripcion: '',
      tipo_tramite: 'NOTIFICACION',
      prioridad: 'MEDIA',
      recurrencia: {
        tipo: 'MENSUAL',
        dia_semana: null,
        dia_mes: 1,
        intervalo_dias: null,
        fecha_inicio: new Date().toISOString().split('T')[0],
        fecha_fin: null,
        hora_notificacion: '09:00'
      },
      responsable_id: '',
      asignados_ids: [],
      grupo_id: '',
      supervisor_id: '',
      dias_anticipacion_notificar: 3,
      notificar_si_no_completado: true,
      tags: [],
      empresa_id: ''
    });
  };

  const handleToggleActivo = async (tramiteId, activo) => {
    try {
      await api.put(`/tramites/recurrentes/${tramiteId}`, { activo: !activo });
      toast.success(activo ? 'Trámite desactivado' : 'Trámite activado');
      loadTramites();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleOpenCompletar = (tramite) => {
    const hoy = new Date().toISOString().split('T')[0];
    setCompletarData({
      tramite,
      fecha_ocurrencia: tramite.proxima_ocurrencia?.split('T')[0] || hoy,
      comentario: '',
      archivo: null
    });
    setShowCompletarDialog(true);
  };

  const handleCompletar = async () => {
    if (!completarData.tramite || !completarData.fecha_ocurrencia) return;
    
    setCompleting(true);
    try {
      let payload = {
        fecha_ocurrencia: completarData.fecha_ocurrencia,
        comentario: completarData.comentario
      };
      
      // Si hay archivo, convertir a base64
      if (completarData.archivo) {
        const reader = new FileReader();
        const base64Promise = new Promise((resolve) => {
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(completarData.archivo);
        });
        payload.archivo_adjunto_base64 = await base64Promise;
        payload.archivo_nombre = completarData.archivo.name;
      }
      
      const res = await api.post(`/tramites/recurrentes/${completarData.tramite.id}/completar`, payload);
      toast.success(`Tarea completada. Próxima: ${new Date(res.data.proxima_ocurrencia).toLocaleDateString('es-ES')}`);
      setShowCompletarDialog(false);
      loadTramites();
      loadData();
    } catch (error) {
      toast.error('Error al completar');
    } finally {
      setCompleting(false);
    }
  };

  const handleVerDetalle = async (tramiteId) => {
    setLoadingDetalle(true);
    setShowDetalleDialog(true);
    try {
      const res = await api.get(`/tramites/recurrentes/${tramiteId}`);
      setTramiteDetalle(res.data);
    } catch (error) {
      toast.error('Error al cargar detalle');
    } finally {
      setLoadingDetalle(false);
    }
  };

  // Eliminar trámite recurrente permanentemente
  const handleDeleteRecurrente = async (tramiteId, permanent = false) => {
    setDeleting(true);
    try {
      const url = permanent 
        ? `/tramites/recurrentes/${tramiteId}?permanent=true` 
        : `/tramites/recurrentes/${tramiteId}`;
      await api.delete(url);
      toast.success(permanent ? 'Trámite eliminado permanentemente' : 'Trámite desactivado');
      setDeleteConfirm(null);
      setShowDetalleDialog(false);
      loadTramites();
      loadData();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  const getTipoIcon = (tipo) => {
    switch (tipo) {
      case 'NOTIFICACION': return Bell;
      case 'REQUERIMIENTO': return FileText;
      case 'VENCIMIENTO': return Calendar;
      case 'REPORTE': return BarChart3;
      default: return Bell;
    }
  };

  const getTipoColor = (tipo) => {
    switch (tipo) {
      case 'NOTIFICACION': return 'bg-blue-100 text-blue-700';
      case 'REQUERIMIENTO': return 'bg-purple-100 text-purple-700';
      case 'VENCIMIENTO': return 'bg-amber-100 text-amber-700';
      case 'REPORTE': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getFrecuenciaLabel = (recurrencia) => {
    if (!recurrencia) return '-';
    switch (recurrencia.tipo) {
      case 'DIARIO': return 'Todos los días';
      case 'SEMANAL': 
        const dias = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
        return `Cada ${dias[recurrencia.dia_semana] || 'semana'}`;
      case 'MENSUAL': return `Día ${recurrencia.dia_mes} de cada mes`;
      case 'TRIMESTRAL': return `Día ${recurrencia.dia_mes} cada trimestre`;
      case 'ANUAL': return `Día ${recurrencia.dia_mes} cada año`;
      case 'PERSONALIZADO': return `Cada ${recurrencia.intervalo_dias} días`;
      default: return recurrencia.tipo;
    }
  };

  const getPrioridadColor = (prioridad) => {
    switch (prioridad) {
      case 'URGENTE': return 'bg-red-100 text-red-700';
      case 'ALTA': return 'bg-amber-100 text-amber-700';
      case 'MEDIA': return 'bg-blue-100 text-blue-700';
      case 'BAJA': return 'bg-slate-100 text-slate-600';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('es-ES');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="tramites-recurrentes-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-100 rounded-lg">
            <CalendarClock size={24} className="text-emerald-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Trámites Recurrentes</h1>
            <p className="text-slate-600">Tareas programadas con notificaciones automáticas</p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/m/tramites/lista')}
            className="border-slate-300"
          >
            Ver Todos los Trámites
          </Button>
          <Button
            onClick={() => setShowNewDialog(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="new-recurrente-btn"
          >
            <Plus size={18} className="mr-2" />
            Nuevo Recurrente
          </Button>
        </div>
      </div>

      {/* Dashboard Stats */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4 bg-white">
            <div className="text-2xl font-bold text-slate-900">{dashboard.total_activos || 0}</div>
            <div className="text-sm text-slate-500">Activos</div>
          </Card>
          <Card className="p-4 bg-green-50 border-green-200">
            <div className="text-2xl font-bold text-green-600">{dashboard.completadas_mes || 0}</div>
            <div className="text-sm text-green-700">Completadas este mes</div>
          </Card>
          <Card className="p-4 bg-amber-50 border-amber-200">
            <div className="text-2xl font-bold text-amber-600">{dashboard.pendientes_mes || 0}</div>
            <div className="text-sm text-amber-700">Pendientes</div>
          </Card>
          <Card className="p-4 bg-blue-50 border-blue-200">
            <div className="text-2xl font-bold text-blue-600">{dashboard.proximos_7_dias?.length || 0}</div>
            <div className="text-sm text-blue-700">Próximos 7 días</div>
          </Card>
        </div>
      )}

      {/* Próximos */}
      {dashboard?.proximos_7_dias?.length > 0 && (
        <Card className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <h3 className="font-medium text-blue-900 mb-3 flex items-center gap-2">
            <Clock size={18} />
            Próximas tareas (7 días)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {dashboard.proximos_7_dias.slice(0, 6).map((item) => {
              const TipoIcon = getTipoIcon(item.tipo_tramite);
              return (
                <div 
                  key={`${item.id}-${item.proxima_fecha}`} 
                  className="flex items-center gap-3 bg-white p-3 rounded-lg border border-blue-100 cursor-pointer hover:border-blue-300"
                  onClick={() => handleVerDetalle(item.id)}
                >
                  <div className={`p-2 rounded-lg ${getTipoColor(item.tipo_tramite)}`}>
                    <TipoIcon size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">{item.titulo}</p>
                    <p className="text-xs text-slate-500">
                      {item.dias_restantes === 0 ? 'Hoy' : `En ${item.dias_restantes} día(s)`} • {formatDate(item.proxima_fecha)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Lista */}
      <Card className="bg-white">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="border-b p-4">
            <div className="flex items-center justify-between">
              <TabsList className="bg-slate-100">
                <TabsTrigger value="activos">Activos</TabsTrigger>
                <TabsTrigger value="inactivos">Inactivos</TabsTrigger>
                <TabsTrigger value="todos">Todos</TabsTrigger>
              </TabsList>
              
              <Button variant="outline" size="sm" onClick={loadTramites}>
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              </Button>
            </div>
          </div>

          <TabsContent value={activeTab} className="mt-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="animate-spin text-emerald-600" size={32} />
              </div>
            ) : tramites.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <CalendarClock size={48} className="mx-auto mb-4 text-slate-300" />
                <p>No hay trámites recurrentes</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => setShowNewDialog(true)}
                >
                  <Plus size={16} className="mr-2" />
                  Crear el primero
                </Button>
              </div>
            ) : (
              <div className="divide-y">
                {tramites.map((t) => {
                  const TipoIcon = getTipoIcon(t.tipo_tramite);
                  return (
                    <div 
                      key={t.id} 
                      className="p-4 hover:bg-slate-50 flex items-center gap-4"
                    >
                      <div className={`p-3 rounded-lg ${getTipoColor(t.tipo_tramite)}`}>
                        <TipoIcon size={20} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-slate-900">{t.titulo}</span>
                          {!t.activo && (
                            <Badge className="bg-slate-100 text-slate-500">Inactivo</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <RefreshCw size={12} />
                            {getFrecuenciaLabel(t.recurrencia)}
                          </span>
                          <span className="flex items-center gap-1">
                            <User size={12} />
                            {t.responsable?.nombre || 'Sin asignar'}
                          </span>
                        </div>
                        {t.proxima_ocurrencia && t.activo && (
                          <p className="text-xs text-emerald-600 mt-1">
                            Próxima: {formatDate(t.proxima_ocurrencia)}
                          </p>
                        )}
                      </div>
                      
                      <Badge className={getPrioridadColor(t.prioridad)}>
                        {t.prioridad}
                      </Badge>
                      
                      <div className="flex items-center gap-1">
                        {t.activo && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenCompletar(t)}
                            className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                          >
                            <Check size={14} className="mr-1" />
                            Completar
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleVerDetalle(t.id)}
                        >
                          <ChevronRight size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActivo(t.id, t.activo)}
                          title={t.activo ? 'Desactivar' : 'Activar'}
                        >
                          {t.activo ? <Pause size={14} className="text-amber-600" /> : <Play size={14} className="text-emerald-600" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirm(t);
                          }}
                          title="Eliminar permanentemente"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </Card>

      {/* Dialog Nuevo Trámite Recurrente - Pantalla Completa */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="bg-white max-w-[95vw] w-full h-[95vh] max-h-[95vh] overflow-hidden flex flex-col">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="flex items-center gap-2 text-xl">
              <CalendarClock className="text-emerald-600" size={24} />
              Nuevo Trámite Recurrente
            </DialogTitle>
            <DialogDescription>
              Cree una tarea que se repita automáticamente con notificaciones
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-6 px-2">
            <div className="max-w-4xl mx-auto space-y-6">
            {/* Información básica */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Label className="text-base font-medium">Título *</Label>
                <Input
                  value={newTramite.titulo}
                  onChange={(e) => setNewTramite({ ...newTramite, titulo: e.target.value })}
                  placeholder="Ej: Presentación F8126 mensual"
                  className="mt-1"
                />
              </div>
              
              <div className="md:col-span-2">
                <Label className="text-base font-medium">Descripción</Label>
                <Textarea
                  value={newTramite.descripcion}
                  onChange={(e) => setNewTramite({ ...newTramite, descripcion: e.target.value })}
                  placeholder="Detalle de la tarea..."
                  rows={3}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label className="text-base font-medium">Tipo de Trámite</Label>
                <Select value={newTramite.tipo_tramite} onValueChange={(v) => setNewTramite({ ...newTramite, tipo_tramite: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {config?.tipos?.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-base font-medium">Prioridad</Label>
                <Select value={newTramite.prioridad} onValueChange={(v) => setNewTramite({ ...newTramite, prioridad: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BAJA">Baja</SelectItem>
                    <SelectItem value="MEDIA">Media</SelectItem>
                    <SelectItem value="ALTA">Alta</SelectItem>
                    <SelectItem value="URGENTE">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Configuración de Recurrencia */}
            <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
              <Label className="font-medium text-emerald-900 mb-3 block">Configuración de Recurrencia</Label>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Frecuencia</Label>
                  <Select 
                    value={newTramite.recurrencia.tipo} 
                    onValueChange={(v) => setNewTramite({ 
                      ...newTramite, 
                      recurrencia: { ...newTramite.recurrencia, tipo: v }
                    })}
                  >
                    <SelectTrigger className="mt-1 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {config?.frecuencias?.map(f => (
                        <SelectItem key={f.id} value={f.id}>{f.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {newTramite.recurrencia.tipo === 'SEMANAL' && (
                  <div>
                    <Label className="text-sm">Día de la semana</Label>
                    <Select 
                      value={String(newTramite.recurrencia.dia_semana || 0)}
                      onValueChange={(v) => setNewTramite({
                        ...newTramite,
                        recurrencia: { ...newTramite.recurrencia, dia_semana: parseInt(v) }
                      })}
                    >
                      <SelectTrigger className="mt-1 bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {config?.dias_semana?.map(d => (
                          <SelectItem key={d.id} value={String(d.id)}>{d.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {['MENSUAL', 'TRIMESTRAL', 'ANUAL'].includes(newTramite.recurrencia.tipo) && (
                  <div>
                    <Label className="text-sm">Día del mes</Label>
                    <Input
                      type="number"
                      min={1}
                      max={31}
                      value={newTramite.recurrencia.dia_mes || 1}
                      onChange={(e) => setNewTramite({
                        ...newTramite,
                        recurrencia: { ...newTramite.recurrencia, dia_mes: parseInt(e.target.value) || 1 }
                      })}
                      className="mt-1 bg-white"
                    />
                  </div>
                )}
                
                {newTramite.recurrencia.tipo === 'PERSONALIZADO' && (
                  <div>
                    <Label className="text-sm">Cada X días</Label>
                    <Input
                      type="number"
                      min={1}
                      value={newTramite.recurrencia.intervalo_dias || 30}
                      onChange={(e) => setNewTramite({
                        ...newTramite,
                        recurrencia: { ...newTramite.recurrencia, intervalo_dias: parseInt(e.target.value) || 30 }
                      })}
                      className="mt-1 bg-white"
                    />
                  </div>
                )}
                
                <div>
                  <Label className="text-sm">Fecha de inicio</Label>
                  <Input
                    type="date"
                    value={newTramite.recurrencia.fecha_inicio}
                    onChange={(e) => setNewTramite({
                      ...newTramite,
                      recurrencia: { ...newTramite.recurrencia, fecha_inicio: e.target.value }
                    })}
                    className="mt-1 bg-white"
                  />
                </div>
                
                <div>
                  <Label className="text-sm">Fecha de fin (opcional)</Label>
                  <Input
                    type="date"
                    value={newTramite.recurrencia.fecha_fin || ''}
                    onChange={(e) => setNewTramite({
                      ...newTramite,
                      recurrencia: { ...newTramite.recurrencia, fecha_fin: e.target.value || null }
                    })}
                    className="mt-1 bg-white"
                  />
                </div>
              </div>
            </div>
            
            {/* Asignación */}
            <div className="space-y-4">
              <Label className="font-medium">Asignación</Label>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Responsable Principal *</Label>
                  <Select value={newTramite.responsable_id} onValueChange={(v) => setNewTramite({ ...newTramite, responsable_id: v })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-sm">Supervisor (para escalamiento)</Label>
                  <Select value={newTramite.supervisor_id || 'none'} onValueChange={(v) => setNewTramite({ ...newTramite, supervisor_id: v === 'none' ? '' : v })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Opcional..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin supervisor</SelectItem>
                      {users.filter(u => ['SUPERVISOR', 'ADMINISTRADOR', 'ADMIN'].includes(u.rol)).map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                  <Label className="text-sm">Asignados adicionales</Label>
                  <div className="border rounded-lg p-3 mt-1 max-h-32 overflow-y-auto space-y-1 bg-slate-50">
                    {users.filter(u => u.id !== newTramite.responsable_id).map(u => (
                      <label key={u.id} className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded">
                        <input
                          type="checkbox"
                          checked={newTramite.asignados_ids?.includes(u.id) || false}
                          onChange={(e) => {
                            const asignados = newTramite.asignados_ids || [];
                            if (e.target.checked) {
                              setNewTramite({ ...newTramite, asignados_ids: [...asignados, u.id] });
                            } else {
                              setNewTramite({ ...newTramite, asignados_ids: asignados.filter(id => id !== u.id) });
                            }
                          }}
                          className="rounded border-slate-300 text-emerald-600"
                        />
                        <span className="text-sm">{u.nombre}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Selector de Grupo (Permisos v2) */}
                {grupos.length > 0 && (
                  <div>
                    <Label className="text-sm">Grupo</Label>
                    <Select value={newTramite.grupo_id || 'auto'} onValueChange={(v) => setNewTramite({ ...newTramite, grupo_id: v === 'auto' ? '' : v })}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Automático (mi grupo)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Automático (mi grupo)</SelectItem>
                        {grupos.map(g => (
                          <SelectItem key={g.id} value={g.id}>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: g.color || '#3b82f6' }} />
                              {g.nombre}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">El grupo determina quién puede ver y gestionar este trámite</p>
                  </div>
                )}
              </div>
            
            {/* Notificaciones */}
            <div className="bg-slate-50 p-4 rounded-lg">
              <Label className="font-medium mb-3 block">Configuración de Notificaciones</Label>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Notificar X días antes</Label>
                  <Input
                    type="number"
                    min={0}
                    value={newTramite.dias_anticipacion_notificar}
                    onChange={(e) => setNewTramite({ ...newTramite, dias_anticipacion_notificar: parseInt(e.target.value) || 3 })}
                    className="mt-1"
                  />
                </div>
                
                <div className="flex items-center gap-2 mt-6">
                  <input
                    type="checkbox"
                    checked={newTramite.notificar_si_no_completado}
                    onChange={(e) => setNewTramite({ ...newTramite, notificar_si_no_completado: e.target.checked })}
                    className="rounded border-slate-300 text-emerald-600"
                  />
                  <Label className="text-sm font-normal">Recordar si no se completó</Label>
                </div>
              </div>
            </div>
          </div>
          </div>
          
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateTramite} disabled={creating} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {creating ? <Loader2 className="animate-spin mr-2" size={16} /> : <Plus size={16} className="mr-2" />}
              Crear Recurrente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Completar */}
      <Dialog open={showCompletarDialog} onOpenChange={setShowCompletarDialog}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="text-emerald-600" size={24} />
              Completar Tarea
            </DialogTitle>
            <DialogDescription>
              {completarData.tramite?.titulo}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Fecha de la ocurrencia</Label>
              <Input
                type="date"
                value={completarData.fecha_ocurrencia}
                onChange={(e) => setCompletarData({ ...completarData, fecha_ocurrencia: e.target.value })}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Comentario / Observación</Label>
              <Textarea
                value={completarData.comentario}
                onChange={(e) => setCompletarData({ ...completarData, comentario: e.target.value })}
                placeholder="Opcional: agregar observación..."
                rows={3}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Adjuntar archivo (opcional)</Label>
              <div className="mt-1">
                <input
                  type="file"
                  onChange={(e) => setCompletarData({ ...completarData, archivo: e.target.files[0] })}
                  className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                />
              </div>
              {completarData.archivo && (
                <p className="text-xs text-emerald-600 mt-1">{completarData.archivo.name}</p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompletarDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCompletar} disabled={completing} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {completing ? <Loader2 className="animate-spin mr-2" size={16} /> : <Check size={16} className="mr-2" />}
              Marcar Completada
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Detalle - Pantalla Completa */}
      <Dialog open={showDetalleDialog} onOpenChange={setShowDetalleDialog}>
        <DialogContent className="bg-white max-w-[95vw] w-full h-[95vh] max-h-[95vh] overflow-hidden flex flex-col">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="flex items-center gap-2 text-xl">
              <CalendarClock className="text-emerald-600" size={24} />
              Detalle del Trámite Recurrente
            </DialogTitle>
          </DialogHeader>
          
          {loadingDetalle ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-emerald-600" size={32} />
            </div>
          ) : tramiteDetalle && (
            <div className="flex-1 overflow-y-auto py-6 px-2">
              <div className="max-w-4xl mx-auto space-y-6">
              {/* Info básica */}
              <div>
                <h3 className="font-semibold text-xl text-slate-900">{tramiteDetalle.titulo}</h3>
                <p className="text-slate-600 mt-1">{tramiteDetalle.descripcion || 'Sin descripción'}</p>
                
                <div className="flex gap-2 mt-3">
                  <Badge className={getTipoColor(tramiteDetalle.tipo_tramite)}>
                    {tramiteDetalle.tipo_tramite}
                  </Badge>
                  <Badge className={getPrioridadColor(tramiteDetalle.prioridad)}>
                    {tramiteDetalle.prioridad}
                  </Badge>
                  <Badge className={tramiteDetalle.activo ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>
                    {tramiteDetalle.activo ? 'Activo' : 'Inactivo'}
                  </Badge>
                </div>
              </div>
              
              {/* Recurrencia */}
              <div className="bg-emerald-50 p-4 rounded-lg">
                <h4 className="font-medium text-emerald-900 mb-2">Configuración de Recurrencia</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Frecuencia:</span>
                    <span className="ml-2 text-slate-900">{getFrecuenciaLabel(tramiteDetalle.recurrencia)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Inicio:</span>
                    <span className="ml-2 text-slate-900">{formatDate(tramiteDetalle.recurrencia?.fecha_inicio)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Fin:</span>
                    <span className="ml-2 text-slate-900">{tramiteDetalle.recurrencia?.fecha_fin ? formatDate(tramiteDetalle.recurrencia.fecha_fin) : 'Sin fecha fin'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Notificar:</span>
                    <span className="ml-2 text-slate-900">{tramiteDetalle.dias_anticipacion_notificar} días antes</span>
                  </div>
                </div>
              </div>
              
              {/* Asignación */}
              <div>
                <h4 className="font-medium text-slate-900 mb-2">Asignación</h4>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                    <User size={14} className="text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium">{tramiteDetalle.responsable?.nombre || 'Sin asignar'}</p>
                    <p className="text-xs text-slate-500">Responsable principal</p>
                  </div>
                </div>
                {tramiteDetalle.asignados?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {tramiteDetalle.asignados.map(a => (
                      <Badge key={a.id} className="bg-slate-100 text-slate-700">
                        {a.nombre}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Próximas ocurrencias */}
              {tramiteDetalle.proximas_ocurrencias?.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
                    <Calendar size={16} />
                    Próximas ocurrencias
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {tramiteDetalle.proximas_ocurrencias.slice(0, 5).map((fecha, idx) => (
                      <Badge key={idx} className="bg-blue-50 text-blue-700 border border-blue-200">
                        {formatDate(fecha)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Historial */}
              {tramiteDetalle.historial_ocurrencias?.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
                    <History size={16} />
                    Historial de completados
                  </h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {tramiteDetalle.historial_ocurrencias.map((h) => (
                      <div 
                        key={h.id} 
                        className={`flex items-center justify-between p-2 rounded ${h.completada ? 'bg-green-50' : 'bg-amber-50'}`}
                      >
                        <div className="flex items-center gap-2">
                          {h.completada ? (
                            <CheckCircle size={14} className="text-green-600" />
                          ) : (
                            <Clock size={14} className="text-amber-600" />
                          )}
                          <span className="text-sm">{formatDate(h.fecha_ocurrencia)}</span>
                        </div>
                        {h.completada && (
                          <span className="text-xs text-slate-500">
                            Por {h.completado_por_nombre} • {formatDate(h.fecha_completado)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            </div>
          )}
          
          <DialogFooter className="border-t pt-4">
            <div className="flex justify-between w-full">
              <Button 
                variant="outline" 
                onClick={() => setDeleteConfirm(tramiteDetalle)}
                className="text-red-600 border-red-300 hover:bg-red-50"
              >
                <Trash2 size={16} className="mr-2" />
                Eliminar
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowDetalleDialog(false)}>
                  Cerrar
                </Button>
                {tramiteDetalle?.activo && (
                  <Button 
                    onClick={() => {
                      setShowDetalleDialog(false);
                      handleOpenCompletar(tramiteDetalle);
                    }}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    <Check size={16} className="mr-2" />
                    Completar
                  </Button>
                )}
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Confirmar Eliminación */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 size={20} />
              Eliminar Trámite Recurrente
            </DialogTitle>
            <DialogDescription>
              ¿Está seguro que desea eliminar el trámite "{deleteConfirm?.titulo}"?
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-3">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <strong>Desactivar:</strong> El trámite dejará de generar ocurrencias pero se conservará el historial.
              </p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">
                <strong>Eliminar permanentemente:</strong> Se borrará el trámite y todo su historial de completados. Esta acción no se puede deshacer.
              </p>
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="w-full sm:w-auto">
              Cancelar
            </Button>
            <Button 
              variant="outline"
              onClick={() => handleDeleteRecurrente(deleteConfirm?.id, false)}
              disabled={deleting}
              className="w-full sm:w-auto border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              {deleting ? <Loader2 className="animate-spin mr-2" size={16} /> : <Pause size={16} className="mr-2" />}
              Solo Desactivar
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleDeleteRecurrente(deleteConfirm?.id, true)}
              disabled={deleting}
              className="w-full sm:w-auto"
            >
              {deleting ? <Loader2 className="animate-spin mr-2" size={16} /> : <Trash2 size={16} className="mr-2" />}
              Eliminar Permanente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TramitesRecurrentes;
